import React, { useEffect, useState } from "react";
import { BarChart3, ChevronRight, Rocket, Route } from "lucide-react";
import { lpApi } from "@/components/portal/lp/lpApi";
import { PortalScreen } from "@/components/portal/lp/PortalScreen";
import { useGedVoice, BAND } from "./gedKit";
import { StepBar, VoiceToggle } from "./GedBits";
import Launchpad from "./Launchpad";
import Baseline from "./Baseline";
import StudyRoadmap from "./StudyRoadmap";

export function useGed(token) {
  const [d, setD] = useState(null);
  useEffect(() => { lpApi(token).get("/portal/resident/ged").then(setD).catch(() => setD({ enabled: false })); }, [token]);
  return [d, (s) => setD((p) => ({ ...p, ...s }))];
}

const AREAS = [
  ["launchpad", "The Launchpad", Rocket, (d) => (d.launchpad_done ? "Done ✓" : `${Object.values(d.launchpad).filter(Boolean).length}/3 parts`)],
  ["baseline", "Diagnostic Baseline", BarChart3, (d) => (d.focus ? `Focus: ${d.focus_name}` : "Add your scores")],
  ["study", "Study Roadmap", Route, (d) => `Step ${Math.min(d.current_step, 5)} of 5`],
];

/** GED Pathway home: progress bar + three big area cards, each opening its own full screen (same flow as Pillars). */
export default function GedPathway({ token, d, setD }) {
  const voice = useGedVoice(token);
  const api = lpApi(token);
  const [open, setOpen] = useState(null);
  const focus = d.subjects.find((s) => s.key === d.focus);
  useEffect(() => { if (!d.launchpad_done && !Object.values(d.launchpad).some(Boolean)) voice.play("welcome"); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const title = AREAS.find((a) => a[0] === open)?.[1];
  return (
    <div className="space-y-4" data-testid="ged-pathway">
      <div className="flex justify-end"><VoiceToggle voice={voice} /></div>
      <StepBar steps={d.steps} />
      {d.ready && <div data-testid="ged-ready-banner" className="rounded-2xl border-2 border-emerald-400 bg-emerald-500/15 p-4 text-lg font-semibold">You're ready to schedule the official GED exam. Talk to your coach.</div>}
      {focus?.score != null && <div className={`rounded-2xl border-2 p-4 ${BAND[focus.band].cls}`} data-testid="ged-focus-card">
        <div className="text-xs uppercase tracking-[0.25em]">Your focus subject</div>
        <div className="flex items-end justify-between mt-1"><span className="text-xl font-bold">{focus.name}</span><span className="text-3xl font-bold">{focus.score}</span></div>
      </div>}
      {AREAS.map(([k, label, Icon, sub]) => (
        <button key={k} type="button" onClick={() => { setOpen(k); if (k === "baseline") voice.play("baseline"); }} data-testid={`ged-area-${k}`}
          className="w-full flex items-center gap-4 rounded-2xl border-2 border-white/15 bg-white/[0.04] p-5 text-left min-h-[88px] active:scale-[0.99] transition-transform">
          <span className="h-14 w-14 shrink-0 grid place-items-center rounded-2xl bg-[#d4a017]/20 text-[#d4a017]"><Icon className="h-7 w-7" /></span>
          <span className="flex-1"><span className="block text-lg font-bold">{label}</span><span className="text-base text-white/65">{sub(d)}</span></span>
          <ChevronRight className="h-6 w-6 text-white/40" />
        </button>
      ))}
      {open && <PortalScreen title={title} onBack={() => setOpen(null)} testid={`ged-screen-${open}`}>
        {open === "launchpad" && <Launchpad d={d} api={api} voice={voice} onChange={setD} />}
        {open === "baseline" && <Baseline d={d} api={api} voice={voice} onChange={setD} />}
        {open === "study" && <StudyRoadmap d={d} token={token} voice={voice} onChange={setD} />}
      </PortalScreen>}
    </div>
  );
}
